import React, { useState, useEffect, useCallback } from 'react';
import apiClient from '../../services/apiClient';
import { useSearchParams } from 'react-router-dom';
import {
  Input, InputNumber, Button, Modal, Spin, Select, Checkbox,
  Table, Tag, Tabs, DatePicker, Alert, Timeline, message,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { useDeliveryForm, Mode } from './hooks/useDeliveryForm';
import type { FC121, FC122, FC123, FC126, ApprovalEvent } from '../../services/c204Api';
import { c204Api } from '../../services/c204Api';
import { useModulePermission } from '../../hooks/usePermission';
import { SharedActionBar } from '../../components/SharedActionBar';
import dayjs from 'dayjs';
import './C204Form.css';
import { BatchAcceptanceBridge, pwc204f1AcceptanceManifest } from '../../features/acceptance';

const { TextArea } = Input;

// ── Helpers ───────────────────────────────────────────────────
/** SC Select — searchable dropdown of approved SC numbers */
const SCSelect: React.FC<{
  value: string; disabled: boolean; onChange: (v: string) => void;
}> = ({ value, disabled, onChange }) => {
  const [options, setOptions] = useState<{ sc_no: string; customer_name: string }[]>([]);
  const [loading, setLoading] = useState(false);

  const fetchSCs = useCallback(async (keyword?: string) => {
    setLoading(true);
    try {
      const params: Record<string, string> = { limit: '30' };
      if (keyword) params.keyword = keyword;
      const res = await apiClient.get('/api/pwd201f1/sc-list', { params });
      setOptions(res.data.data || []);
    } catch { /* ignore */ }
    setLoading(false);
  }, []);

  useEffect(() => { fetchSCs(); }, [fetchSCs]);

  return (
    <Select
      showSearch allowClear disabled={disabled} loading={loading}
      value={value || undefined} placeholder="選擇已核准 S/C"
      style={{ width: '100%' }}
      filterOption={false}
      onSearch={v => fetchSCs(v)}
      onChange={v => onChange(v || '')}
      options={options.map(o => ({
        value: o.sc_no,
        label: `${o.sc_no}${o.customer_name ? ' - ' + o.customer_name : ''}`,
      }))}
    />
  );
};

/** Generic Lookup Select — fetches from /api/lookup/:type */
const LookupSelect: React.FC<{
  lookupType: string; label: string; value: string; disabled: boolean;
  onChange: (v: string) => void;
}> = ({ lookupType, value, disabled, label, onChange }) => {
  const [options, setOptions] = useState<{ code: string; name: string }[]>([]);
  const [loading, setLoading] = useState(false);

  const fetchOptions = useCallback(async (keyword?: string) => {
    setLoading(true);
    try {
      const params: Record<string, string> = { page: '1', size: '50' };
      if (keyword) params.keyword = keyword;
      const res = await apiClient.get(`/api/lookup/${lookupType}`, { params });
      setOptions(res.data.data || []);
    } catch { /* ignore */ }
    setLoading(false);
  }, [lookupType]);

  useEffect(() => { fetchOptions(); }, [fetchOptions]);

  return (
    <div>
      {label && <label className="block text-xs text-gray-600 mb-1">{label}</label>}
      <Select
        showSearch allowClear disabled={disabled} loading={loading}
        value={value || undefined} placeholder={label ? `選擇${label}` : '選擇...'}
        style={{ width: '100%' }}
        filterOption={false}
        onSearch={v => fetchOptions(v)}
        onChange={v => onChange(v || '')}
        options={options.map(o => ({ value: o.code, label: `${o.code} - ${o.name}` }))}
      />
    </div>
  );
};

const dateToInput = (v: string | null | undefined): dayjs.Dayjs | null => {
  if (!v) return null;
  const d = dayjs(v);
  return d.isValid() ? d : null;
};

const fmt = (v: number | null | undefined): string => {
  if (v == null || v === 0) return '0.00';
  return v.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const statusColor = (s: string): string => {
  switch (s) {
    case 'P': return 'green';
    case 'D': return 'blue';
    case 'C': return 'red';
    default: return 'default';
  }
};

// (PermBtn and getStatusLabel removed — SharedActionBar handles these)

// ── Component ─────────────────────────────────────────────────
const C204Form: React.FC = () => {
  const [searchParams] = useSearchParams();
  const reviewNo = searchParams.get('review');

  const {
    mode, isEditing, loading,
    records, current, currentIdx,
    formData, details, lookupNames,
    updateField, updateDetailField,
    navFirst, navPrev, navNext, navLast,
    startAdd, startEdit, cancel, save,
    doDelete, statusLabel, setDetails, addDetailRow, deleteDetailRow,
    resolveSingleName, ntdTotalWithTax,
    INVOICE_OPTIONS, DELIVERY_TYPE_OPTIONS,
    getFieldError,
    handleSubmit, handleApprove, handleReject,
    navigate,
    lastApiResponse,
  } = useDeliveryForm(reviewNo);

  const { canEdit, canApprove } = useModulePermission('pwc204f1');

  const [searchText, setSearchText] = useState('');

  // History modal state
  const [historyVisible, setHistoryVisible] = useState(false);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyEvents, setHistoryEvents] = useState<ApprovalEvent[]>([]);

  // BR-33: 選 SC 後自動帶入主檔 + 明細
  const handleSCSelected = useCallback(async (scNo: string) => {
    try {
      const sc = await c204Api.importFromSC(scNo, formData.fc120_11 || undefined);
      if (!sc) return;
      // 主檔欄位
      updateField('fc120_12', sc.fc120_12 || '');
      updateField('fc120_17', sc.fc120_17 || '');
      updateField('fc120_18', sc.fc120_18 || '');
      updateField('fc120_19', sc.fc120_19 || 0);
      const today = new Date().toISOString();
      if (!formData.fc120_11) updateField('fc120_11', today);
      if (!formData.fc120_32) updateField('fc120_32', today);
      // resolve names
      resolveSingleName('customer', 'customer', sc.fc120_12 || '');
      resolveSingleName('department', 'department', sc.fc120_17 || '');
      resolveSingleName('currency', 'currency', sc.fc120_18 || '');
      // 明細
      const scDetails = sc.details || [];
      if (scDetails.length > 0) {
        const newDetails = scDetails;
        setDetails(newDetails);
        message.success(`已從 S/C ${scNo} 帶入 ${newDetails.length} 筆產品明細`);
      }
    } catch { /* ignore */ }
  }, [formData, updateField, resolveSingleName, setDetails]);

  const handleDelete = () => {
    if (!current) return;
    Modal.confirm({
      title: '確認刪除',
      content: `確定要刪除交運單「${current.fc120_01}」？所有明細將一併刪除。`,
      okText: '刪除', okType: 'danger', cancelText: '取消',
      onOk: doDelete,
    });
  };

  const handleSubmitClick = () => {
    if (!current) return;
    Modal.confirm({
      title: '確認送審',
      content: `確定送審此交運單「${current.fc120_01}」？`,
      okText: '送審',
      cancelText: '取消',
      onOk: handleSubmit,
    });
  };

  const handleApproveClick = () => {
    if (!current) return;
    Modal.confirm({
      title: '確認核准',
      content: `確定核准此交運單「${current.fc120_01}」？`,
      okText: '核准',
      cancelText: '取消',
      onOk: async () => {
        const ok = await handleApprove();
        if (ok && reviewNo) navigate('/erp/approvals');
      },
    });
  };

  const handleRejectClick = () => {
    if (!current) return;
    Modal.confirm({
      title: '確認退回',
      content: `確定退回此交運單「${current.fc120_01}」？`,
      okText: '退回',
      okType: 'danger',
      cancelText: '取消',
      onOk: async () => {
        const ok = await handleReject();
        if (ok && reviewNo) navigate('/erp/approvals');
      },
    });
  };

  const handleWriteoffClick = () => {
    if (!current || isEditing) return;
    Modal.confirm({
      title: '確認沖銷',
      content: `確定要沖銷此單據 ${current.fc120_01}？沖銷後可重新送審。`,
      okText: '沖銷',
      okType: 'danger',
      cancelText: '取消',
      onOk: async () => {
        try {
          const resp = await c204Api.writeoff(current.fc120_01);
          message.success(resp.message || '沖銷成功');
          window.location.reload();
        } catch (err: any) {
          const msg = err?.response?.data?.error || (err instanceof Error ? err.message : '沖銷失敗');
          message.error(msg);
        }
      },
    });
  };

  const handlePrint = () => {
    if (!current?.fc120_01) return;
    const url = c204Api.getPrintUrl(current.fc120_01);
    window.open(url, '_blank');
  };

  const handleShowHistory = async () => {
    if (!current?.fc120_01) return;
    setHistoryVisible(true);
    setHistoryLoading(true);
    try {
      const events = await c204Api.getHistory(current.fc120_01);
      setHistoryEvents(events);
    } catch {
      message.error('無法載入審核歷史');
    } finally {
      setHistoryLoading(false);
    }
  };

  const modeLabel = mode === 'add' ? '新增' : mode === 'edit' ? '修改' : '瀏覽';

  // ── Detail columns ─────────────────────────────────────────
  const detailColumns: ColumnsType<Partial<FC121>> = [
    { title: <span data-contract-field="fc121_02">項次</span>, dataIndex: 'fc121_02', key: 'seq', width: 60 },
    { title: <span data-contract-field="fc121_10">S/C No</span>, dataIndex: 'fc121_10', key: 'sc', width: 100 },
    { title: <span data-contract-field="fc121_11">產品代號</span>, dataIndex: 'fc121_11', key: 'prod', width: 100 },
    { title: <span data-contract-field="fc121_12">色號</span>, dataIndex: 'fc121_12', key: 'color', width: 80 },
    { title: <span data-contract-field="fc121_13">產品規格</span>, dataIndex: 'fc121_13', key: 'spec', ellipsis: true },
    { title: <span data-contract-field="fc121_16">交運數量</span>, dataIndex: 'fc121_16', key: 'qty', width: 100,
      render: (val: number, _rec: Partial<FC121>, idx: number) =>
        isEditing ? (
          <InputNumber value={val} min={0} size="small" style={{ width: '100%' }}
            onChange={v => updateDetailField(idx, 'fc121_16', v ?? 0)} />
        ) : (val ?? 0),
    },
    { title: <span data-contract-field="fc121_17">單位</span>, dataIndex: 'fc121_17', key: 'unit', width: 60 },
    { title: <span data-contract-field="fc121_18">交運單價</span>, dataIndex: 'fc121_18', key: 'price', width: 100,
      render: (val: number, _rec: Partial<FC121>, idx: number) =>
        isEditing ? (
          <InputNumber value={val} min={0} step={0.01} size="small" style={{ width: '100%' }}
            onChange={v => updateDetailField(idx, 'fc121_18', v ?? 0)} />
        ) : fmt(val),
    },
    { title: <span data-contract-field="fc121_19">交運金額</span>, dataIndex: 'fc121_19', key: 'amount', width: 110,
      render: (val: number) => fmt(val),
    },
    { title: <span data-contract-field="fc121_14">S/C項次</span>, dataIndex: 'fc121_14', key: 'scseq', width: 70 },
    { title: <span data-contract-field="fc121_20">S/C訂量</span>, dataIndex: 'fc121_20', key: 'scqty', width: 100,
      render: (val: number) => fmt(val),
    },
    { title: <span data-contract-field="fc121_30">補布數量</span>, dataIndex: 'fc121_30', key: 'patch', width: 100,
      render: (val: number, _rec: Partial<FC121>, idx: number) =>
        isEditing ? (
          <InputNumber value={val} min={0} size="small" style={{ width: '100%' }}
            onChange={v => updateDetailField(idx, 'fc121_30', v ?? 0)} />
        ) : (val ?? 0),
    },
    ...(isEditing ? [{
      title: '操作', key: 'action', width: 60,
      render: (_: unknown, __: Partial<FC121>, idx: number) => (
        <Button size="small" danger type="link" onClick={() => deleteDetailRow(idx)}>刪除</Button>
      ),
    }] : []),
  ];

  // ── Batch sub-table columns ────────────────────────────────
  const batchColumns: ColumnsType<FC122> = [
    { title: '批號項次', dataIndex: 'fc122_03', key: 'bseq', width: 70 },
    { title: '批號', dataIndex: 'fc122_11', key: 'batch', width: 120 },
    { title: '交運數量', dataIndex: 'fc122_12', key: 'bqty', width: 100 },
    { title: '交運單位', dataIndex: 'fc122_14', key: 'bunit', width: 80 },
    { title: '換算率', dataIndex: 'fc122_15', key: 'rate', width: 80 },
    { title: '庫存單位', dataIndex: 'fc122_17', key: 'sunit', width: 80 },
    { title: '換算後數量', dataIndex: 'fc122_18', key: 'cqty', width: 100 },
    { title: '補布', dataIndex: 'fc122_22', key: 'patch', width: 60,
      render: (val: boolean) => val ? '是' : '',
    },
  ];

  const expandedRowRender = (record: Partial<FC121>) => {
    const batches = record.batches || [];
    return (
      <div>
        {/* Page2 standalone fields: color info */}
        <div className="grid grid-cols-5 gap-2 mb-2 text-xs">
          <div><span className="text-gray-500">色號項次:</span> {record.fc121_22 || '-'}</div>
          <div><span className="text-gray-500">色號序號:</span> {record.fc121_23 ?? '-'}</div>
          <div><span className="text-gray-500">中文色名:</span> {record.fc121_24 || '-'}</div>
          <div><span className="text-gray-500">英文色名:</span> {record.fc121_25 || '-'}</div>
          <div><span className="text-gray-500">印花配色:</span> {record.fc121_28 || '-'}</div>
        </div>
        {record.fc121_13 && (
          <div className="text-xs mb-2"><span className="text-gray-500">產品規格:</span> {record.fc121_13}</div>
        )}
        {batches.length === 0 ? (
          <span className="text-gray-400">無批號明細</span>
        ) : (
          <Table<FC122>
            dataSource={batches}
            columns={batchColumns}
            rowKey={(r) => `${r.fc122_01}-${r.fc122_02}-${r.fc122_03}`}
            size="small"
            pagination={false}
          />
        )}
      </div>
    );
  };

  return (
    <Spin spinning={loading}>
      <div className="max-w-6xl mx-auto p-4">
        {/* Review banner */}
        {reviewNo && (
          <Alert
            message="您正在審核此單據"
            description={`單號: ${reviewNo}。核准或退回後將自動返回待審核清單。`}
            type="warning"
            showIcon
            banner
            style={{ marginBottom: 12 }}
          />
        )}

        {/* Header */}
        <h2 className="text-xl font-bold mb-3">
          交運單建檔 (C204)
          <span className="ml-3 text-sm font-normal text-gray-500">
            [{modeLabel}] {records.length > 0 ? `${currentIdx + 1} / ${records.length}` : '無資料'}
            {current && (
              <Tag className="ml-2" color={statusColor(current.fc120_80)}>
                {statusLabel(current.fc120_80)}
              </Tag>
            )}
          </span>
        </h2>

        {/* Search bar */}
        <div className="flex items-center gap-2 mb-3">
          <Input placeholder="輸入單號或客戶查詢" value={searchText}
            onChange={e => setSearchText(e.target.value)} disabled={isEditing}
            style={{ width: 260 }} maxLength={40} />
          <Button onClick={() => searchText.trim() && window.location.reload()} disabled={isEditing}>查詢</Button>
        </div>

        {/* Tabs */}
        <Tabs defaultActiveKey="1" items={[
          { key: '1', label: '基本資料', children: renderTab1(
            formData, isEditing, lookupNames, updateField, resolveSingleName,
            INVOICE_OPTIONS, DELIVERY_TYPE_OPTIONS, ntdTotalWithTax, mode, getFieldError,
            handleSCSelected,
          )},
          { key: '2', label: '產品明細', children: (
            <div>
              <div className="flex items-center gap-4 mb-2">
                <span className="text-xs text-gray-500">總項數: <strong>{formData.fc120_27 ?? details.length}</strong></span>
                {isEditing && (
                  <Button size="small" type="dashed" onClick={addDetailRow}>+ 新增明細行</Button>
                )}
              </div>
              <Table<Partial<FC121>>
                dataSource={details}
                columns={detailColumns}
                rowKey={(r) => `${r.fc121_01 || 'new'}-${r.fc121_02 || 0}`}
                size="small" pagination={false} scroll={{ y: 360 }}
                expandable={{ expandedRowRender }}
              />
            </div>
          )},
          { key: '3', label: '帳款資訊', children: renderTab3(formData, current, ntdTotalWithTax) },
          { key: '4', label: '異常說明', children: renderTab4(formData, isEditing, updateField) },
        ]} />

        {/* Footer info */}
        {current && mode === 'browse' && (
          <div className="text-xs text-gray-400 mt-3">
            立單人: {current.fc120_91} ({lookupNames.salesperson})
            {' | '}輸單人員: {current.fc120_94} ({lookupNames.entryPerson})
            {' | '}日期: {current.fc120_92 ? dayjs(current.fc120_92).format('YYYY-MM-DD') : '-'}
          </div>
        )}

        {/* Spacer for fixed bottom bar */}
        <div style={{ height: 64 }} />

        {/* History Modal */}
        <Modal
          title="審核歷史"
          open={historyVisible}
          onCancel={() => setHistoryVisible(false)}
          footer={<Button onClick={() => setHistoryVisible(false)}>關閉</Button>}
          width={480}
        >
          {historyLoading ? (
            <Spin />
          ) : historyEvents.length === 0 ? (
            <p style={{ color: '#999' }}>尚無審核歷史記錄</p>
          ) : (
            <Timeline
              items={historyEvents.map(ev => ({
                color: ev.action === '核准' ? 'green' : ev.action === '退回' ? 'red' : 'blue',
                children: (
                  <div>
                    <strong>{ev.action}</strong>
                    {ev.user && <span style={{ marginLeft: 8, color: '#666' }}>by {ev.user}</span>}
                    {ev.date && (
                      <span style={{ marginLeft: 8, color: '#999' }}>
                        {dayjs(ev.date).format('YYYY-MM-DD HH:mm')}
                      </span>
                    )}
                    {ev.note && <div style={{ color: '#555', marginTop: 2 }}>{ev.note}</div>}
                  </div>
                ),
              }))}
            />
          )}
        </Modal>

        {/* ===== Fixed Bottom Action Bar (SharedActionBar) ===== */}
        <SharedActionBar
          acceptanceActionIds={{
            create: 'PWC204-ACT-CREATE',
            update: 'PWC204-ACT-UPDATE',
            delete: 'PWC204-ACT-DELETE',
            submit: 'PWC204-ACT-SUBMIT',
            approve: 'PWC204-ACT-DIRECT-APPROVE',
            reject: 'PWC204-ACT-DIRECT-REJECT',
            writeoff: 'PWC204-ACT-DIRECT-WRITEOFF',
          }}
          mode={mode}
          isEditing={isEditing}
          hasRecord={!!current}
          currentIdx={currentIdx}
          recordCount={records.length}
          onNavFirst={navFirst}
          onNavPrev={navPrev}
          onNavNext={navNext}
          onNavLast={navLast}
          onAdd={startAdd}
          onModify={startEdit}
          onDelete={handleDelete}
          onSave={save}
          onCancel={cancel}
          approval={{
            statusCode: (current?.fc120_80 || '').trim(),
            isApproved: (current?.fc120_80 || '').trim() === 'P',
            isPending: ['S', 'D'].includes((current?.fc120_80 || '').trim()), // 新碼 S + 舊碼 D
            isLocked: ['P', 'S', 'D'].includes((current?.fc120_80 || '').trim()),
          }}
          onSubmit={handleSubmitClick}
          onApprove={handleApproveClick}
          onReject={handleRejectClick}
          onWriteoff={handleWriteoffClick}
          onHistory={handleShowHistory}
          onPrint={handlePrint}
          onExit={() => navigate('/erp')}
          canEdit={canEdit}
          canApprove={canApprove}
        />
        <div>
          <BatchAcceptanceBridge
            manifest={pwc204f1AcceptanceManifest}
            records={records}
            selectedKeys={current ? [current.fc120_01] : []}
            keyOf={(item) => item.fc120_01}
            queryExact={(key) => c204Api.get(key)}
            lastApiResponse={lastApiResponse}
            loading={loading}
            onAutoFill={() => {
              if (records.length > 0) navFirst();
            }}
            autoFillDisabled={records.length === 0}
            autoFillReason="安全載入第一張既有交運單，不會新增、修改、送審、核准或沖銷。"
            screenDraft={{ mode, master: formData, details }}
          />
        </div>
      </div>
    </Spin>
  );
};

// ── Tab 1: Basic Info ─────────────────────────────────────────
function renderTab1(
  formData: Partial<import('../../services/c204Api').FC120>,
  isEditing: boolean,
  lookupNames: Record<string, string>,
  updateField: (f: string, v: unknown) => void,
  resolveSingleName: (k: string, t: string, c: string) => void,
  invoiceOpts: { value: number; label: string }[],
  deliveryTypeOpts: { value: number; label: string }[],
  ntdTotalWithTax: number,
  mode: Mode,
  getFieldError: (field: string) => string | undefined,
  onSCSelected?: (scNo: string) => void,
) {
  const E = (field: string) => {
    const err = getFieldError(field);
    return err ? <span style={{ color: '#ff4d4f', fontSize: 11, marginLeft: 6, fontWeight: 'normal' }}>{err}</span> : null;
  };

  return (
    <div className="grid grid-cols-4 gap-3">
      {/* Row 1 */}
      <div>
        <label className="block text-xs text-gray-600 mb-1" data-contract-field="fc120_01">交運單號</label>
        <Input value={formData.fc120_01 || (mode === 'add' ? '(自動產生)' : '')} disabled />
      </div>
      <div>
        <label className="block text-xs text-gray-600 mb-1" data-contract-field="fc120_10">S/C NO{E('fc120_10')}</label>
        <SCSelect value={formData.fc120_10 || ''} disabled={!isEditing || mode === 'edit'}
          onChange={v => { updateField('fc120_10', v); if (v && onSCSelected) onSCSelected(v); }} />
      </div>
      <div>
        <label className="block text-xs text-gray-600 mb-1" data-contract-field="fc120_11">交運日期{E('fc120_11')}</label>
        <DatePicker value={dateToInput(formData.fc120_11)} disabled={!isEditing}
          onChange={(_d, ds) => updateField('fc120_11', ds ? `${ds}T00:00:00Z` : null)}
          style={{ width: '100%' }} />
      </div>
      <div>
        <label className="block text-xs text-gray-600 mb-1" data-contract-field="fc120_48">出貨通知單號{E('fc120_48')}</label>
        <Input value={formData.fc120_48 || ''} disabled={!isEditing}
          onChange={e => updateField('fc120_48', e.target.value)} maxLength={8} />
      </div>

      {/* Row 2 */}
      <div>
        <label className="block text-xs text-gray-600 mb-1" data-contract-field="fc120_12">客戶代號</label>
        <div className="flex gap-1 items-center">
          <Input value={formData.fc120_12 || ''} disabled style={{ flex: 1 }} />
          <span className="text-xs text-gray-500 truncate" style={{ maxWidth: 120 }}>
            {lookupNames.customer || ''}
          </span>
        </div>
      </div>
      <div>
        <label className="block text-xs text-gray-600 mb-1" data-contract-field="fc120_17">部門代號</label>
        <div className="flex gap-1 items-center">
          <Input value={formData.fc120_17 || ''} disabled style={{ flex: 1 }} />
          <span className="text-xs text-gray-500 truncate" style={{ maxWidth: 120 }}>
            {lookupNames.department || ''}
          </span>
        </div>
      </div>
      <div>
        <label className="block text-xs text-gray-600 mb-1" data-contract-field="fc120_18">幣別</label>
        <Input value={formData.fc120_18 || ''} disabled />
      </div>
      <div>
        <label className="block text-xs text-gray-600 mb-1" data-contract-field="fc120_19">匯率{E('fc120_19')}</label>
        <InputNumber value={formData.fc120_19 ?? 1} disabled style={{ width: '100%' }}
          precision={4} />
      </div>

      {/* Row 3 */}
      <div>
        <label className="block text-xs text-gray-600 mb-1" data-contract-field="fc120_14">庫位代號{E('fc120_14')}</label>
        <div>
          <LookupSelect lookupType="warehouse" label="" value={formData.fc120_14 || ''} disabled={!isEditing}
            onChange={v => updateField('fc120_14', v)} />
        </div>
      </div>
      <div>
        <label className="block text-xs text-gray-600 mb-1" data-contract-field="fc120_21">收貨公司代號{E('fc120_21')}</label>
        <div className="flex gap-1 items-center">
          <Input value={formData.fc120_21 || ''} disabled={!isEditing}
            onChange={e => updateField('fc120_21', e.target.value)}
            onBlur={() => resolveSingleName('receiver', 'customer', formData.fc120_21 || '')}
            style={{ flex: 1 }} />
          <span className="text-xs text-gray-500 truncate" style={{ maxWidth: 120 }}>
            {lookupNames.receiver || ''}
          </span>
        </div>
      </div>
      <div>
        <label className="block text-xs text-gray-600 mb-1" data-contract-field="fc120_13">交運類別</label>
        <Select value={formData.fc120_13 ?? 1} onChange={v => updateField('fc120_13', v)}
          disabled={!isEditing} options={deliveryTypeOpts} style={{ width: '100%' }} />
      </div>
      <div>
        <label className="block text-xs text-gray-600 mb-1" data-contract-field="fc120_291">發票種類{E('fc120_291')}</label>
        <Select value={formData.fc120_291 ?? 1} onChange={v => updateField('fc120_291', v)}
          disabled={!isEditing} options={invoiceOpts} style={{ width: '100%' }} />
      </div>

      {/* Row 4 */}
      <div className="col-span-2">
        <label className="block text-xs text-gray-600 mb-1" data-contract-field="fc120_23">收貨工廠地址</label>
        <Input value={formData.fc120_23 || ''} disabled={!isEditing}
          onChange={e => updateField('fc120_23', e.target.value)} maxLength={50} />
      </div>
      <div>
        <label className="block text-xs text-gray-600 mb-1" data-contract-field="fc120_44">轉客戶寄庫庫位{E('fc120_44')}</label>
        <div className="flex gap-1 items-center">
          <Input value={formData.fc120_44 || ''} disabled={!isEditing}
            onChange={e => updateField('fc120_44', e.target.value)}
            onBlur={() => resolveSingleName('consignWarehouse', 'warehouse', formData.fc120_44 || '')}
            maxLength={5} style={{ flex: 1 }} />
          <span className="text-xs text-gray-500 truncate" style={{ maxWidth: 100 }}>
            {lookupNames.consignWarehouse || ''}
          </span>
        </div>
      </div>
      <div>
        <label className="block text-xs text-gray-600 mb-1">客訴單號</label>
        <Input value={formData.fc120_49 || ''} disabled={!isEditing}
          onChange={e => updateField('fc120_49', e.target.value)} maxLength={8} />
      </div>

      {/* Row 4.5 — checkbox */}
      <div className="col-span-4 flex items-center gap-4">
        <Checkbox checked={!!formData.fc120_29} disabled={!isEditing}
          onChange={e => updateField('fc120_29', e.target.checked)}>
          開立發票
        </Checkbox>
      </div>

      {/* Row 5 */}
      <div>
        <label className="block text-xs text-gray-600 mb-1" data-contract-field="fc120_30">報關日期</label>
        <DatePicker value={dateToInput(formData.fc120_30)} disabled={!isEditing}
          onChange={(_d, ds) => updateField('fc120_30', ds ? `${ds}T00:00:00Z` : null)}
          style={{ width: '100%' }} />
      </div>
      <div>
        <label className="block text-xs text-gray-600 mb-1" data-contract-field="fc120_31">報關匯率</label>
        <InputNumber value={formData.fc120_31 ?? 0} disabled={!isEditing}
          onChange={v => updateField('fc120_31', v)} precision={4} style={{ width: '100%' }} />
      </div>
      <div>
        <label className="block text-xs text-gray-600 mb-1" data-contract-field="fc120_28">最晚批號繳庫日期{E('fc120_28')}</label>
        <DatePicker value={dateToInput(formData.fc120_28)} disabled={!isEditing}
          onChange={(_d, ds) => updateField('fc120_28', ds ? `${ds}T00:00:00Z` : null)}
          style={{ width: '100%' }} />
      </div>
      <div>
        <label className="block text-xs text-gray-600 mb-1" data-contract-field="fc120_32">打單日期</label>
        <DatePicker value={dateToInput(formData.fc120_32)} disabled={!isEditing}
          onChange={(_d, ds) => updateField('fc120_32', ds ? `${ds}T00:00:00Z` : null)}
          style={{ width: '100%' }} />
      </div>

      {/* Row 6 */}
      <div>
        <label className="block text-xs text-gray-600 mb-1" data-contract-field="ui_salesperson_name">S/C 業務</label>
        <Input value={lookupNames.salesperson || '-'} disabled />
      </div>
      <div>
        <label className="block text-xs text-gray-600 mb-1" data-contract-field="fc120_94">輸單人員</label>
        <Input value={`${formData.fc120_94 || ''}${lookupNames.entryPerson ? '  ' + lookupNames.entryPerson : ''}`} disabled />
      </div>
    </div>
  );
}

// ── Tab 3: Account Info ───────────────────────────────────────
function renderTab3(
  formData: Partial<import('../../services/c204Api').FC120>,
  current: import('../../services/c204Api').FC120 | null,
  ntdTotalWithTax: number,
) {
  const s = current || formData;
  const items: [string, string, string][] = [
    ['交運淨額', fmt(s.fc120_20), 'fc120_20'],
    ['稅額', fmt(s.fc120_16), 'fc120_16'],
    ['含稅總金額', fmt(s.fc120_201), 'fc120_201'],
    ['台幣含稅總金額', fmt(Math.round(ntdTotalWithTax)), 'ui_ntd_total_with_tax'],
    ['匯率', s.fc120_19?.toFixed(4) ?? '1.0000', 'fc120_19'],
    ['修改日期', s.fc120_92 ? dayjs(s.fc120_92).format('YYYY-MM-DD') : '-', 'fc120_92'],
  ];

  const payments: FC123[] = (current as import('../../services/c204Api').FC120 & { payments?: FC123[] })?.payments || [];
  const invoices: FC126[] = (current as import('../../services/c204Api').FC120 & { invoices?: FC126[] })?.invoices || [];

  const paymentCols: ColumnsType<FC123> = [
    { title: <span data-contract-field="fc123_11">繳款日期</span>, dataIndex: 'fc123_11', key: 'pdate', width: 120,
      render: (v: string | null) => v ? dayjs(v).format('YYYY-MM-DD') : '-' },
    { title: <span data-contract-field="fc123_02">繳款單號</span>, dataIndex: 'fc123_02', key: 'pno', width: 120 },
    { title: <span data-contract-field="fc123_12">繳款金額</span>, dataIndex: 'fc123_12', key: 'pamt', width: 120,
      render: (v: number) => fmt(v) },
  ];

  const invoiceCols: ColumnsType<FC126> = [
    { title: <span data-contract-field="fc126_02">發票號碼</span>, dataIndex: 'fc126_02', key: 'ino', width: 120 },
    { title: <span data-contract-field="fc126_11">發票日期</span>, dataIndex: 'fc126_11', key: 'idate', width: 120,
      render: (v: string | null) => v ? dayjs(v).format('YYYY-MM-DD') : '-' },
    { title: <span data-contract-field="fc126_12">發票金額</span>, dataIndex: 'fc126_12', key: 'iamt', width: 120,
      render: (v: number) => fmt(v) },
  ];

  return (
    <div>
      <div className="grid grid-cols-3 gap-3 mb-4">
        {items.map(([label, val, fieldId]) => (
          <div key={label}>
            <label className="text-xs text-gray-600" data-contract-field={fieldId}>{label}</label>
            <Input value={val} disabled />
          </div>
        ))}
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <h4 className="text-sm font-semibold mb-2">繳款記錄</h4>
          <Table<FC123>
            dataSource={payments}
            columns={paymentCols}
            rowKey={(r) => `${r.fc123_01}-${r.fc123_02}`}
            size="small" pagination={false}
            locale={{ emptyText: '無繳款記錄' }}
          />
        </div>
        <div>
          <h4 className="text-sm font-semibold mb-2">發票記錄</h4>
          <Table<FC126>
            dataSource={invoices}
            columns={invoiceCols}
            rowKey={(r) => `${r.fc126_01}-${r.fc126_02}`}
            size="small" pagination={false}
            locale={{ emptyText: '無發票記錄' }}
          />
        </div>
      </div>
    </div>
  );
}

// ── Tab 4: Abnormal Notes ────────────────────────────────────
function renderTab4(
  formData: Partial<import('../../services/c204Api').FC120>,
  isEditing: boolean,
  updateField: (f: string, v: unknown) => void,
) {
  return (
    <div className="grid grid-cols-1 gap-4">
      <div>
        <label className="block text-xs text-gray-600 mb-1" data-contract-field="fc120_40">異常說明-業務</label>
        <TextArea value={formData.fc120_40 || ''} disabled={!isEditing} rows={4}
          onChange={e => updateField('fc120_40', e.target.value)} />
      </div>
      <div>
        <label className="block text-xs text-gray-600 mb-1" data-contract-field="fc120_41">異常說明-主管</label>
        <TextArea value={formData.fc120_41 || ''} disabled={!isEditing} rows={4}
          onChange={e => updateField('fc120_41', e.target.value)} />
      </div>
    </div>
  );
}

export default C204Form;
